import type { PageLink } from "../../api/pages";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { SectionHeader } from "../../components/ui/section-header";
import { useLanguage } from "../../hooks/useLanguage";

export type LinkFormState = {
  label: string;
  url: string;
};

type LinksListProps = {
  links: PageLink[];
  canManageLinks: boolean;
  onAdd: () => void;
  onEdit: (link: PageLink) => void;
  onDelete: (link: PageLink) => void;
};

function linkTargetUrl(link: PageLink) {
  return link.url || "";
}

export function LinksList({
  links,
  canManageLinks,
  onAdd,
  onEdit,
  onDelete,
}: LinksListProps) {
  const { t } = useLanguage();

  return (
    <Card className="space-y-4">
      <SectionHeader
        title={t.pageBuilder.linksList.title}
        action={
          <Button type="button" size="micro" onClick={() => onAdd()} disabled={!canManageLinks}>
            {t.pageBuilder.linksList.add}
          </Button>
        }
      />
      {links.length > 0 ? (
        <div className="space-y-2">
          {links.map(link => (
            <div
              key={link.id}
              className="flex flex-col gap-2 rounded-lg border border-neutral-200 p-4 md:flex-row md:items-center md:justify-between"
            >
              <div>
                <p className="text-sm font-semibold text-neutral-800">
                  {link.label || t.pageBuilder.linksList.untitled}
                </p>
                <p className="text-xs text-neutral-500 break-all mt-0.5">
                  {linkTargetUrl(link) || t.pageBuilder.linksList.unconfigured}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="micro"
                  onClick={() => onEdit(link)}
                >
                  {t.pageBuilder.linksList.edit}
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  size="micro"
                  onClick={() => onDelete(link)}
                >
                  {t.pageBuilder.linksList.remove}
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-neutral-500">
          {canManageLinks
            ? t.pageBuilder.linksList.empty.withPermission
            : t.pageBuilder.linksList.empty.withoutPermission}
        </p>
      )}
    </Card>
  );
}
